import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import palette from 'src/theme/palette';
import { useSidebar } from 'src/providers/sidebar/useSidebar';
import {
  DELETE_INVENTORY_REPLENISHMENT,
  GET_INVENTORY_REPLENISHMENTS,
  UPDATE_INVENTORY_REPLENISHMENT,
} from 'src/utils/gql/inventoryQueries';
import { DEFAULT_REPLENISHMENT_NOTES, getNotesPreview } from './utils';

type ReplenishmentRow = {
  replenishmentId: string;
  productId: string;
  productTitle: string;
  imageUrl?: string | null;
  restockedDate?: string | null;
  restockedQuantity?: number | null;
  notes?: string | null;
};

const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  overflow: 'visible',
  [theme.breakpoints.up('sm')]: {
    marginLeft: '240px',
    width: 'calc(100% - 240px)',
    maxWidth: 'calc(100% - 240px)',
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '220px',
    width: 'calc(100% - 220px)',
    maxWidth: 'calc(100% - 220px)',
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
    marginTop: '60px',
    maxWidth: '100%',
  },
}));

const ContentContainer = styled(Box)(() => ({
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: '16px',
  boxSizing: 'border-box',
  overflow: 'visible',
  minHeight: '100%',
}));

const RestockedPage: React.FC = () => {
  const theme = useTheme();
  const { collapsed: sidebarCollapsed } = useSidebar();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const controlHeight = 40;
  const controlRadius = 2;
  const containerSx = sidebarCollapsed
    ? {
        [theme.breakpoints.up('sm')]: {
          marginLeft: '72px',
          width: 'calc(100% - 72px)',
          maxWidth: 'calc(100% - 72px)',
        },
      }
    : undefined;

  const { data, loading, error } = useQuery(GET_INVENTORY_REPLENISHMENTS, {
    variables: { limit: 200 },
    fetchPolicy: 'network-only',
  });
  const [updateReplenishment] = useMutation(UPDATE_INVENTORY_REPLENISHMENT);
  const [deleteReplenishment] = useMutation(DELETE_INVENTORY_REPLENISHMENT);

  const rows: ReplenishmentRow[] = data?.getInventoryReplenishments ?? [];

  const [rowMenuAnchor, setRowMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<ReplenishmentRow | null>(null);
  const rowMenuOpen = Boolean(rowMenuAnchor);

  const [editOpen, setEditOpen] = useState(false);
  const [editDate, setEditDate] = useState<string>('');
  const [editQty, setEditQty] = useState<string>('');
  const [editNotes, setEditNotes] = useState<string>('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const NOTES_PREVIEW_MAX_LEN = 60;

  const safeRows = useMemo(() => {
    const d = [...rows];
    d.sort((a, b) => {
      const da = String(a.restockedDate || '').localeCompare(
        String(b.restockedDate || ''),
      );
      return -da;
    });
    return d;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return safeRows;
    return safeRows.filter((r) =>
      String(r.productTitle || '')
        .toLowerCase()
        .includes(q),
    );
  }, [safeRows, searchTerm]);

  const totalRestockedQuantity = useMemo(
    () =>
      filteredRows.reduce((total, row) => {
        if (row.restockedQuantity == null) return total;
        const qty = Number(row.restockedQuantity);
        return Number.isFinite(qty) ? total + qty : total;
      }, 0),
    [filteredRows],
  );

  const openRowMenu = (
    e: React.MouseEvent<HTMLElement>,
    row: ReplenishmentRow,
  ) => {
    setSelectedRow(row);
    setRowMenuAnchor(e.currentTarget);
  };
  const closeRowMenu = () => setRowMenuAnchor(null);

  const openEdit = () => {
    if (!selectedRow) return;
    setEditDate(
      String(
        selectedRow.restockedDate || new Date().toISOString().slice(0, 10),
      ),
    );
    setEditQty(
      selectedRow.restockedQuantity != null
        ? String(selectedRow.restockedQuantity)
        : '',
    );
    const notesNormalized = selectedRow.notes
      ? String(selectedRow.notes).trim()
      : '';
    setEditNotes(
      notesNormalized ? notesNormalized : DEFAULT_REPLENISHMENT_NOTES,
    );
    setEditOpen(true);
    closeRowMenu();
  };

  const openDeleteConfirm = () => {
    setDeleteConfirmOpen(true);
    closeRowMenu();
  };

  const saveEdit = async () => {
    if (!selectedRow) return;
    const qtyRaw = editQty.trim();
    const qty = qtyRaw ? Number(qtyRaw) : null;
    const notesNormalized = editNotes.trim();
    await updateReplenishment({
      variables: {
        input: {
          replenishmentId: selectedRow.replenishmentId,
          restockedDate: editDate,
          restockedQuantity:
            qty != null && Number.isFinite(qty)
              ? Math.max(0, Math.floor(qty))
              : null,
          notes:
            notesNormalized && notesNormalized.length > 0
              ? notesNormalized
              : DEFAULT_REPLENISHMENT_NOTES,
        },
      },
      refetchQueries: [
        { query: GET_INVENTORY_REPLENISHMENTS, variables: { limit: 200 } },
      ],
    });
    setEditOpen(false);
  };

  const confirmDelete = async () => {
    if (!selectedRow) return;
    await deleteReplenishment({
      variables: { replenishmentId: selectedRow.replenishmentId },
      refetchQueries: [
        { query: GET_INVENTORY_REPLENISHMENTS, variables: { limit: 200 } },
      ],
    });
    setDeleteConfirmOpen(false);
  };

  return (
    <PageContainer sx={containerSx}>
      <ContentContainer>
        <Box
          sx={{
            height: 4,
            borderRadius: '0 0 4px 4px',
            background: `linear-gradient(90deg, ${palette.primary.main}, ${palette.primary.light})`,
            mb: 2,
            mx: -0.5,
          }}
        />

        <Box
          sx={{
            mb: 2,
            pb: 2,
            borderBottom: '1px solid',
            borderColor: palette.grey[200],
            background: `linear-gradient(180deg, ${palette.primary.main}05 0%, ${palette.primary.main}02 25%, transparent 70%)`,
            mt: -0.5,
            pt: 2,
            px: 2,
            mx: -2,
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 1,
              background: `linear-gradient(90deg, transparent, ${palette.primary.main}18, transparent)`,
              opacity: 0.6,
            },
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: '999px',
              bgcolor: `${palette.primary.main}18`,
              border: '1px solid',
              borderColor: `${palette.primary.main}35`,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: palette.primary.main,
                flexShrink: 0,
                boxShadow: `0 0 0 2px ${palette.primary.main}40`,
              }}
            />
            <Typography
              sx={{
                color: palette.primary.main,
                fontWeight: 700,
                letterSpacing: 1.1,
                fontSize: '0.7rem',
                textTransform: 'uppercase',
              }}
            >
              Inventory
            </Typography>
          </Box>

          <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
            Restocked
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Products you marked as <strong>Restocked</strong>, with restock date
            and quantity.
          </Typography>

          <Box sx={{ mt: 2, width: { xs: '100%', sm: 320 }, flexShrink: 0 }}>
            <Typography
              component="label"
              sx={{
                display: 'block',
                mb: 0.5,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: palette.grey[500],
              }}
            >
              Search
            </Typography>
            <TextField
              fullWidth
              placeholder="Search products…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  alignItems: 'center',
                  height: controlHeight,
                  maxHeight: controlHeight,
                  boxSizing: 'border-box',
                  borderRadius: controlRadius,
                },
                '& .MuiInputBase-input': {
                  py: 0.5,
                  lineHeight: 1.43,
                  boxSizing: 'border-box',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: palette.grey[600] }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                sx: { bgcolor: 'white', borderRadius: 2 },
              }}
            />
          </Box>
        </Box>

        {error ? (
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography color="error">{error.message}</Typography>
            </CardContent>
          </Card>
        ) : loading ? (
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography color="text.secondary">
                Loading restocked items…
              </Typography>
            </CardContent>
          </Card>
        ) : safeRows.length === 0 ? (
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography color="text.secondary">
                No restocked items yet. Mark a product as{' '}
                <strong>Restocked</strong> in Inventory to see it here.
              </Typography>
            </CardContent>
          </Card>
        ) : filteredRows.length === 0 ? (
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography color="text.secondary">
                No results for <strong>{searchTerm}</strong>.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <CardContent sx={{ p: 0 }}>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: palette.grey[100] }}>
                      <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Restocked date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Quantity
                      </TableCell>
                      <TableCell sx={{ width: 48 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((r) => (
                      <TableRow key={r.replenishmentId} hover>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 1.5,
                                bgcolor: palette.grey[100],
                                border: `1px solid ${palette.grey[200]}`,
                                overflow: 'hidden',
                                position: 'relative',
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                component="img"
                                src={r.imageUrl || ''}
                                alt={r.productTitle}
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  const target =
                                    e.currentTarget as HTMLImageElement;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  const fallback = parent?.querySelector(
                                    '[data-image-fallback]',
                                  ) as HTMLElement | null;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                  display: r.imageUrl ? 'block' : 'none',
                                }}
                              />
                              <Box
                                data-image-fallback
                                sx={{
                                  display: r.imageUrl ? 'none' : 'flex',
                                  position: 'absolute',
                                  inset: 0,
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  color: palette.grey[600],
                                  fontWeight: 700,
                                }}
                              >
                                📦
                              </Box>
                            </Box>
                            <Box>
                              <Typography
                                sx={{ fontWeight: 800, fontSize: 13 }}
                              >
                                {r.productTitle}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const { shouldShow, preview, full } =
                              getNotesPreview(r.notes, NOTES_PREVIEW_MAX_LEN);
                            if (!shouldShow) return '—';

                            return (
                              <Tooltip title={full}>
                                <Typography
                                  sx={{
                                    maxWidth: 260,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: 12,
                                    fontWeight: 600,
                                  }}
                                >
                                  {preview}
                                </Typography>
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {r.restockedDate ? String(r.restockedDate) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {r.restockedQuantity != null
                            ? Number(r.restockedQuantity).toLocaleString(
                                'it-IT',
                              )
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => openRowMenu(e, r)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{
                        bgcolor: palette.background.tableRow,
                        fontWeight: 600,
                      }}
                    >
                      <TableCell colSpan={3} sx={{ fontWeight: 600 }}>
                        Totals
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {totalRestockedQuantity.toLocaleString('it-IT')}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        <Menu
          anchorEl={rowMenuAnchor}
          open={rowMenuOpen}
          onClose={closeRowMenu}
        >
          <MenuItem onClick={openEdit}>Edit</MenuItem>
          <MenuItem onClick={openDeleteConfirm} sx={{ color: 'error.main' }}>
            Delete
          </MenuItem>
        </Menu>

        <Dialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit replenishment</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'grid', gap: 2 }}>
              <TextField
                label="Restock date"
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Quantity"
                type="number"
                value={editQty}
                onChange={(e) => setEditQty(e.target.value)}
                fullWidth
                inputProps={{ min: 0, step: 1 }}
              />
              <TextField
                label="Notes"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button
              onClick={saveEdit}
              variant="contained"
              sx={{ bgcolor: palette.primary.main }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete replenishment?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This will remove the event from history.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </ContentContainer>
    </PageContainer>
  );
};

export default RestockedPage;
